import { useState, useRef } from "react";
import { uploadFileToVps, deleteFileFromVps } from "@/lib/vpsStorage";
import type { UploadFolder } from "@/lib/vpsStorage";

interface Props {
  value: string | null;
  onChange: (url: string | null) => void;
  tenantId: string;
  entityType: string;
  entityId?: string;
  label?: string;
  accept?: string;
  showPreview?: boolean;
  previewHeight?: string;
  previewType?: "image" | "audio";
  fileId?: string | null;
  onFileIdChange?: (fileId: string | null) => void;
  module?: string;
  className?: string;
  helperText?: string;
  disabled?: boolean;
  /** VPS folder/category for organization */
  folder?: UploadFolder;
  /** Allowed MIME types for client-side validation. E.g. ["image/jpeg", "image/png"] */
  allowedTypes?: string[];
  /** Max file size in MB */
  maxSizeMB?: number;
  /** Optional: called with full metadata on successful upload */
  onUploaded?: (url: string, metadata: { fileId?: string; filename?: string; size?: number; mimeType?: string; path?: string }) => void;
  /** Optional: called on upload error */
  onUploadError?: (error: string) => void;
}

export default function VpsUploadField({
  value,
  onChange,
  tenantId,
  entityType,
  entityId,
  label,
  accept = "image/*",
  showPreview = true,
  previewHeight = "h-32",
  previewType = "image",
  fileId,
  onFileIdChange,
  module,
  className = "",
  helperText,
  disabled = false,
  folder,
  allowedTypes,
  maxSizeMB,
  onUploaded,
  onUploadError,
}: Props) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!tenantId) return;
    setError(null);
    setUploading(true);
    setProgress(10);
    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        entityType,
        entityId,
        module,
        folder,
        allowedTypes,
        maxSizeMB,
      });
      if (result.success && result.url) {
        setProgress(100);
        onChange(result.url);
        if (onFileIdChange) {
          onFileIdChange(result.fileId ?? null);
        }
        onUploaded?.(result.url, {
          fileId: result.fileId,
          filename: result.filename,
          size: result.size,
          mimeType: result.mime_type,
          path: result.path,
        });
      } else {
        const err = result.error || "Upload failed";
        setError(err);
        onUploadError?.(err);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setError(msg);
      onUploadError?.(msg);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemove = async () => {
    if (fileId) {
      try {
        await deleteFileFromVps({ fileId, tenantId, silent: true });
      } catch {
        // ignore delete error
      }
    }
    onChange(null);
    if (onFileIdChange) onFileIdChange(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          {label}
        </label>
      )}

      {value && showPreview ? (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
          {previewType === "image" ? (
            <div className={`w-full ${previewHeight}`}>
              <img
                src={value}
                alt="Preview"
                className="w-full h-full object-cover object-top"
              />
            </div>
          ) : previewType === "audio" ? (
            <div className="p-4">
              <audio controls src={value} className="w-full h-8" />
            </div>
          ) : null}
          <button
            onClick={handleRemove}
            disabled={disabled}
            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/50 hover:bg-red-500 text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            title="Remove"
          >
            <i className="ri-delete-bin-line text-xs"></i>
          </button>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !disabled && inputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
            dragOver
              ? "border-[#C8A96E] bg-[#C8A96E]/5"
              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {uploading ? (
            <div className="space-y-2">
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#C8A96E] transition-all"
                  style={{ width: `${progress || 10}%` }}
                />
              </div>
              <p className="text-slate-500 text-xs">Uploading via secure proxy…</p>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                <i className="ri-upload-cloud-2-line text-xl"></i>
              </div>
              <p className="text-slate-500 text-xs font-medium">
                Click or drag file here
              </p>
              <p className="text-slate-400 text-[10px] mt-0.5">
                {accept === "image/*" && "JPG, PNG, WEBP"}
                {accept === "audio/*" && "MP3, OGG, WAV"}
                {accept === "image/*,audio/*" && "Images or audio"}
                {accept !== "image/*" && accept !== "audio/*" && accept !== "image/*,audio/*" && accept}
                {maxSizeMB && ` · max ${maxSizeMB} MB`}
              </p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleChange}
            disabled={disabled || uploading}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-1.5 text-red-500 text-xs">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line"></i>
          </span>
          {error}
        </div>
      )}
      {helperText && !error && (
        <p className="text-slate-400 text-xs">{helperText}</p>
      )}
    </div>
  );
}